import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation, useNavigate } from "react-router-dom";
import GuestStatusCard from "../components/GuestStatusCard";
import GuestToastStack from "../components/GuestToastStack";
import SplitBillBar from "../components/SplitBillBar";
import { useData } from "../context/DataProvider";
import useGuestFeedback from "../hooks/useGuestFeedback";
import { buildGuestLoginRedirectOptions, isAuthRecoveryError } from "../utils/authRecovery";
import { getConfig } from "../config";
import { formatPrice } from "../utils/formatPrice";
import { formatModifierSummary } from "../utils/modifierUtils";
import {
  getApiErrorMessage,
  getQrSessionMessage,
  getRecoveryReason,
  isQrSessionError,
  requestBill,
} from "../services/apiService";

const LOCK_COLORS = ["#1f5b4d", "#b67a3d", "#5b4d8f", "#c94040", "#2d7db3"];

const OrderSummaryPage = () => {
  const { guestAuthEnabled } = getConfig();
  const { existingOrders, menu, fetchMenuData, fetchExistingOrdersData, clearExistingOrders } = useData();
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [isValidVenueTable, setIsValidVenueTable] = useState(false);
  const [validationLoading, setValidationLoading] = useState(true);
  const [validationError, setValidationError] = useState("");
  const [validationErrorCode, setValidationErrorCode] = useState(null);
  const [billRequested, setBillRequested] = useState(false);
  const [requestingBill, setRequestingBill] = useState(false);
  const [checkoutSuccessBanner, setCheckoutSuccessBanner] = useState(null);
  const { feedbackItems, pushFeedback, dismissFeedback } = useGuestFeedback();

  const [splitMode, setSplitMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [lockGroups, setLockGroups] = useState([]);

  const toggleItem = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const lockSelection = () => {
    const color = LOCK_COLORS[lockGroups.length % LOCK_COLORS.length];
    setLockGroups((prev) => [...prev, { keys: new Set(selectedKeys), color }]);
    setSelectedKeys(new Set());
  };

  const clearLockGroups = () => {
    setLockGroups([]);
    setSelectedKeys(new Set());
  };

  const exitSplitMode = () => {
    setSplitMode(false);
    setSelectedKeys(new Set());
    setLockGroups([]);
  };

  const beginSummaryLogin = async () => {
    try {
      await loginWithRedirect(buildGuestLoginRedirectOptions(location.pathname + location.search));
    } catch (error) {
      pushFeedback({
        variant: "danger",
        title: "Giriş başlatılamadı",
        message: getApiErrorMessage(error, "Giriş sayfasına yönlendirme sırasında hata oluştu."),
      });
    }
  };

  const getItemPrice = (item) =>
    item.item_price ??
    item.item_price_snapshot ??
    menu.find((menuItem) => menuItem.id === item.item_id)?.price ??
    0;

  const totalPrice = existingOrders.reduce(
    (sum, order) =>
      sum + (order.items || []).reduce((s, item) => s + getItemPrice(item) * item.quantity, 0),
    0
  );

  const totalLineItems = existingOrders.reduce(
    (sum, order) => sum + (order.items || []).reduce((s, item) => s + item.quantity, 0),
    0
  );

  const splitTotal = existingOrders.reduce(
    (sum, order) =>
      sum +
      (order.items || []).reduce((s, item, idx) => {
        const key = `${order.id}-${item.item_id}-${idx}`;
        return selectedKeys.has(key) ? s + getItemPrice(item) * item.quantity : s;
      }, 0),
    0
  );

  const splitUnitCount = existingOrders.reduce(
    (sum, order) =>
      sum +
      (order.items || []).reduce((s, item, idx) => {
        const key = `${order.id}-${item.item_id}-${idx}`;
        return selectedKeys.has(key) ? s + item.quantity : s;
      }, 0),
    0
  );

  const lockGroupTotals = lockGroups.map((group) =>
    existingOrders.reduce(
      (sum, order) =>
        sum +
        (order.items || []).reduce((s, item, idx) => {
          const key = `${order.id}-${item.item_id}-${idx}`;
          return group.keys.has(key) ? s + getItemPrice(item) * item.quantity : s;
        }, 0),
      0
    )
  );

  const lockedTotal = lockGroupTotals.reduce((s, t) => s + t, 0);
  const remainingTotal = totalPrice - lockedTotal;
  const nextLockColor = LOCK_COLORS[lockGroups.length % LOCK_COLORS.length];

  useEffect(() => {
    if (!location.state?.checkoutFeedback) return;
    setCheckoutSuccessBanner(location.state.checkoutFeedback);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    let isActive = true;

    const initializeApp = async () => {
      try {
        await Promise.all([fetchMenuData(), fetchExistingOrdersData()]);
        if (!isActive) return;
        setIsValidVenueTable(true);
        setValidationError("");
      } catch (error) {
        if (!isActive) return;
        console.error("Error validating session:", error);
        clearExistingOrders();
        setIsValidVenueTable(false);
        setValidationErrorCode(error?.code || null);
        setValidationError(
          isQrSessionError(error)
            ? getQrSessionMessage(error.code)
            : "Masa oturumu doğrulanamadı. Lütfen QR kodunu tekrar tarayın."
        );
      } finally {
        if (isActive) setValidationLoading(false);
      }
    };

    initializeApp();
    return () => { isActive = false; };
  }, [clearExistingOrders, fetchExistingOrdersData, fetchMenuData]);

  const handleRequestBill = async () => {
    setRequestingBill(true);
    try {
      const token = guestAuthEnabled ? await getAccessTokenSilently() : null;
      await requestBill(token);
      setBillRequested(true);
    } catch (error) {
      if (guestAuthEnabled && isAuthRecoveryError(error)) {
        await beginSummaryLogin();
        return;
      }
      if (error.code === "REQUEST_ALREADY_EXISTS") {
        setBillRequested(true);
      } else if (isQrSessionError(error)) {
        clearExistingOrders();
        setIsValidVenueTable(false);
        setValidationErrorCode(error.code);
        setValidationError(getQrSessionMessage(error.code));
      } else {
        pushFeedback({
          variant: "danger",
          title: "Hesap isteği gönderilemedi",
          message: getApiErrorMessage(error, "Hesap isteği gönderilirken hata oluştu."),
        });
      }
    } finally {
      setRequestingBill(false);
    }
  };

  if (validationLoading) {
    return (
      <div className="guest-state-layout">
        <GuestStatusCard
          variant="info"
          title="Siparişleriniz yükleniyor"
          message="Masa oturumu ve mevcut siparişler kontrol ediliyor."
          busy
        />
      </div>
    );
  }

  if (!isValidVenueTable) {
    return (
      <div className="guest-state-layout">
        <GuestStatusCard
          variant="warning"
          title="Geçersiz QR Kod"
          message={validationError || "Lütfen geçerli bir QR kod tarayın veya masa numaranızı kontrol edin."}
          actionLabel="QR Kodunu Yeniden Tara"
          onAction={() => navigate(`/qr-scan?reason=${getRecoveryReason({ code: validationErrorCode })}`)}
        >
          <p className="guest-recovery-note mb-0">
            Yardıma ihtiyacınız olursa lütfen garsonumuzdan destek isteyin.
          </p>
        </GuestStatusCard>
      </div>
    );
  }

  return (
    <div className={`container px-3 px-lg-4 guest-page${splitMode ? " has-split-bar" : ""}`}>
      <GuestToastStack items={feedbackItems} onDismiss={dismissFeedback} />

      <div className="guest-page-header">
        <div className="guest-page-eyebrow">Masa takibi</div>
        <h1 className="guest-page-title">Sipariş Özeti</h1>
        <p className="guest-page-subtitle">
          Bu masa için ilettiğiniz siparişler aşağıda listelenir.
        </p>
      </div>

      {checkoutSuccessBanner && (
        <GuestStatusCard
          variant={checkoutSuccessBanner.variant}
          title={checkoutSuccessBanner.title}
          message={checkoutSuccessBanner.message}
          dismissible
          onDismiss={() => setCheckoutSuccessBanner(null)}
        />
      )}

      {existingOrders.length > 0 ? (
        <div className="guest-summary-stack">
          {requestingBill && (
            <GuestStatusCard
              variant="info"
              title="Hesap isteği gönderiliyor"
              message="Lütfen sayfadan ayrılmayın."
              busy
            />
          )}

          {billRequested && (
            <GuestStatusCard
              variant="success"
              title="Hesap isteğiniz alındı"
              message="Garsonumuz kısa süre içinde masanıza gelecektir."
            />
          )}

          {splitMode && (
            <div className="guest-split-banner">
              <span className="guest-split-banner-icon">✂️</span>
              <div className="guest-split-banner-text">
                <strong>Hesap bölme modu</strong>
                <span>Payınıza düşen ürünlere dokunun</span>
              </div>
            </div>
          )}

          {splitMode && lockGroups.length > 0 && (
            <div className="guest-split-group-summary">
              {lockGroups.map((group, i) => (
                <div key={i} className="guest-split-group-row">
                  <span className="guest-split-group-dot" style={{ background: group.color }} />
                  <span className="guest-split-group-label">Kişi {i + 1}</span>
                  <span className="guest-split-group-amount" style={{ color: group.color }}>
                    {formatPrice(lockGroupTotals[i])}
                  </span>
                </div>
              ))}
              <div className="guest-split-group-remaining">
                <span>Kalan</span>
                <span>{formatPrice(remainingTotal)}</span>
              </div>
              <button className="guest-split-group-reset" onClick={clearLockGroups}>
                Sıfırla
              </button>
            </div>
          )}

          <div>
            {existingOrders.map((order) => {
              const orderTotal = (order.items || []).reduce(
                (sum, item) => sum + getItemPrice(item) * item.quantity,
                0
              );

              return (
                <div key={order.id} className="card guest-summary-order">
                  <div className="card-body p-3">
                    <div className="guest-summary-order-header">
                      <div>
                        <span className="guest-summary-order-id">Sipariş #{order.id}</span>
                        <h5 className="guest-summary-order-title mb-0">Masanıza iletilen sipariş</h5>
                      </div>
                      <span className="guest-price">{formatPrice(orderTotal)}</span>
                    </div>
                    {order.notes && (
                      <div className="guest-summary-note">
                        <strong className="guest-summary-note-label">Sipariş notu</strong>
                        <span>{order.notes}</span>
                      </div>
                    )}
                    {(order.items || []).map((item, index) => {
                      const itemKey = `${order.id}-${item.item_id}-${index}`;
                      const lockedGroup = lockGroups.find((g) => g.keys.has(itemKey));
                      const isLocked = !!lockedGroup;
                      const isSelected = !isLocked && selectedKeys.has(itemKey);
                      const isDimmed = splitMode && !isLocked && isSelected;
                      const menuItem = menu.find((candidate) => candidate.id === item.item_id);
                      const itemName = item.item_name || item.item_name_snapshot || menuItem?.name || "Bilinmeyen Ürün";
                      const itemPrice = getItemPrice(item);
                      const itemKdv = item.item_kdv ?? item.item_kdv_snapshot ?? menuItem?.kdv ?? 0;
                      const itemTotal = itemPrice * item.quantity;

                      return (
                        <div
                          key={itemKey}
                          className={[
                            "guest-summary-line-item",
                            splitMode && !isLocked && "is-selectable",
                            isDimmed && "is-dimmed",
                            isLocked && "is-locked",
                          ].filter(Boolean).join(" ")}
                          style={
                            isLocked
                              ? { borderLeft: `3px solid ${lockedGroup.color}`, background: `${lockedGroup.color}14` }
                              : isSelected
                              ? { borderLeft: `3px solid ${nextLockColor}`, background: `${nextLockColor}14` }
                              : undefined
                          }
                          onClick={splitMode && !isLocked ? () => toggleItem(itemKey) : undefined}
                          role={splitMode && !isLocked ? "checkbox" : undefined}
                          aria-checked={splitMode && !isLocked ? isSelected : undefined}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold">{itemName}</h6>
                              {item.modifiers?.length ? (
                                <div className="small text-muted mb-1">{formatModifierSummary(item.modifiers)}</div>
                              ) : null}
                              <div className="guest-summary-line-meta">
                                {formatPrice(itemPrice)} × {item.quantity}
                              </div>
                              <div className="guest-summary-line-badges">
                                {itemKdv > 0 && (
                                  <span className="badge rounded-pill bg-light text-dark">KDV %{itemKdv}</span>
                                )}
                                <span className="badge rounded-pill text-bg-secondary">{item.quantity} adet</span>
                                {item.status && (
                                  <span className={`badge rounded-pill ${{
                                    pending: "bg-warning text-dark",
                                    preparing: "bg-primary text-white",
                                    completed: "bg-success text-white",
                                    cancelled: "bg-secondary text-white",
                                  }[item.status] ?? "bg-light text-dark"}`}>
                                    {{
                                      pending: "Beklemede",
                                      preparing: "Hazırlanıyor",
                                      completed: "Teslim Edildi",
                                      cancelled: "İptal",
                                    }[item.status] ?? item.status}
                                  </span>
                                )}
                                {isLocked && (
                                  <span
                                    className="guest-split-locked-badge"
                                    style={{ background: lockedGroup.color }}
                                  >
                                    Alındı ✓
                                  </span>
                                )}
                              </div>
                              {item.notes && (
                                <div className="guest-summary-note-inline">
                                  <strong className="d-block">Ürün notu</strong>
                                  <span>{item.notes}</span>
                                </div>
                              )}
                            </div>
                            <div className="d-flex align-items-center ms-3 gap-2 flex-shrink-0">
                              <div className="guest-cart-item-total">{formatPrice(itemTotal)}</div>
                              {splitMode && !isLocked && (
                                <div
                                  className={`guest-split-check${isSelected ? " is-checked" : ""}`}
                                  style={isSelected ? { background: nextLockColor, borderColor: nextLockColor } : undefined}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {!splitMode && (
            <div className="card guest-summary-total">
              <div className="card-body p-4">
                <div className="guest-summary-total-row">
                  <span className="text-muted">Toplam ({totalLineItems} ürün)</span>
                  <span className="guest-price">{formatPrice(totalPrice)}</span>
                </div>
                <hr className="my-2" />
                <div className="guest-summary-total-row mb-0">
                  <span className="fw-bold">Ödenecek Tutar</span>
                  <span className="h4 mb-0 fw-bold text-success">{formatPrice(totalPrice)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="guest-summary-actions mb-4">
            {!billRequested && !splitMode && (
              <Button
                variant="success"
                size="lg"
                onClick={handleRequestBill}
                disabled={requestingBill}
                className="py-3"
              >
                {requestingBill ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Gönderiliyor...
                  </>
                ) : (
                  "Hesap İste"
                )}
              </Button>
            )}
            {!splitMode && (
              <Button
                variant="outline-primary"
                onClick={() => setSplitMode(true)}
                className="py-2 guest-split-trigger"
              >
                Hesabı Böl
              </Button>
            )}
            {!splitMode && (
              <Button variant="outline-secondary" onClick={() => navigate("/order")} className="py-2">
                Siparişe Devam Et
              </Button>
            )}
          </div>

          {!splitMode && (
            <div className="guest-summary-info mb-4">
              Hesap talebiniz gönderildikten sonra garsonumuz masanıza gelecektir.
            </div>
          )}
        </div>
      ) : (
        <div className="card guest-summary-empty">
          <div className="card-body">
            <h5 className="guest-summary-empty-title">Henüz sipariş vermediniz</h5>
            <p className="guest-summary-empty-copy">Menüden sipariş vererek başlayın.</p>
            <Button variant="primary" size="lg" onClick={() => navigate("/order")} className="px-4">
              Menüye Dön
            </Button>
          </div>
        </div>
      )}

      {splitMode && (
        <SplitBillBar
          selectedCount={selectedKeys.size}
          selectedUnitCount={splitUnitCount}
          selectedTotal={splitTotal}
          remainingTotal={remainingTotal}
          hasLockedGroups={lockGroups.length > 0}
          onLock={lockSelection}
          onClear={() => setSelectedKeys(new Set())}
          onClose={exitSplitMode}
        />
      )}
    </div>
  );
};

export default OrderSummaryPage;
